import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { PoweredByCuddles } from 'components/Cuddles/PoweredBy/PoweredByCuddles';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { Alert } from 'components/UI/Alert/Alert';
import { walkingBookingErrorCopy } from 'components/OnlineBooking/Walking/errors';
import { useMarketplace } from 'hooks/useMarketplace';
import { useOnlineBookingState } from 'hooks/useOnlineBookingState';
import React from 'react';
import { useSelector } from 'react-redux';
import { dispatch } from 'store';
import {
    navigateToStep,
    updateOnlineBookingBookingItems
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { OnlineBookingFooter } from 'components/OnlineBooking/Footer/OnlineBookingFooter';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { WalkingDateTimePicker } from 'components/OnlineBooking/Walking/DatePicker/WalkingDateTimePicker';
import { ONLINE_BOOKING_SESSION_SELECTORS } from 'reducers/onlineBooking/OnlineBookingSelectors';
import { OnlineBookingSessionItem, OnlineBookingStep, SessionStepProps } from 'types/OnlineBooking';
import { getBusinessArea, trackDateTimeSelected } from 'utils/analytics';

export const OnlineBookingWalkingDatePicker: React.FC<SessionStepProps> = React.memo(props => {
    const { businessArea } = props;
    const marketplace = useMarketplace();
    const { isPending } = useOnlineBookingState();
    const sessionSelectors = ONLINE_BOOKING_SESSION_SELECTORS[businessArea];

    const selectedTime = useSelector(sessionSelectors.time);
    const selectedDate = useSelector(sessionSelectors.date);
    const preferredStaff = useSelector(sessionSelectors.preferredStaff);
    const walkingItems = useSelector(sessionSelectors.items);
    const lastBookingError = useSelector(sessionSelectors.lastBookingError);
    const lastBookingErrorItemUuid = useSelector(sessionSelectors.lastBookingErrorItemUuid);

    const lastBookingErrorPetName = walkingItems.find(
        item => item.uuid === lastBookingErrorItemUuid
    )?.pet.name;

    const isValid = selectedDate !== undefined && selectedTime !== undefined;

    const backHandler = React.useCallback(() => {
        const previousStep = marketplace.onlineBookingPreferences.allowSelectStaff
            ? OnlineBookingStep.StaffList
            : OnlineBookingStep.ServiceList;
        dispatch(navigateToStep(previousStep));
    }, [marketplace.onlineBookingPreferences.allowSelectStaff]);

    const dateTimePickerChangeHandler = React.useCallback(
        (updatedItems: Array<OnlineBookingSessionItem>) => {
            dispatch(updateOnlineBookingBookingItems(updatedItems));
        },
        []
    );

    const submitHandler = React.useCallback(() => {
        if (selectedDate && selectedTime) {
            trackDateTimeSelected({
                marketplace_slug: marketplace.slug,
                business_area: getBusinessArea(walkingItems),
                booking_date: selectedDate.format('YYYY-MM-DD'),
                time_of_day: selectedTime.hour < 12 ? 'morning' : 'afternoon'
            });
        }

        dispatch(navigateToStep(OnlineBookingStep.ServiceCategory));
    }, [selectedDate, selectedTime, marketplace.slug, walkingItems]);

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h2 underlined w-full mb-5">Select date & time</h1>

                {lastBookingError && !selectedTime && (
                    <Alert
                        variant="error"
                        icon={faInfoCircle}
                        text={walkingBookingErrorCopy(
                            lastBookingError,
                            lastBookingErrorPetName,
                            businessArea
                        )}
                        className="mb-5"
                    />
                )}

                <WalkingDateTimePicker
                    businessArea={businessArea}
                    preferredStaff={preferredStaff}
                    items={walkingItems}
                    onChange={dateTimePickerChangeHandler}
                />

                <div className="mt-auto hide-on-mobile-and-tablet pt-6">
                    <PoweredByCuddles />
                </div>
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <OnlineBookingFooter
                        disabled={isPending || !isValid}
                        onSubmit={submitHandler}
                    />
                }
            >
                <div className="flex flex-col gap-5 hide-on-mobile-and-tablet">
                    <MarketplaceInfo inverted marketplace={marketplace} />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
});

OnlineBookingWalkingDatePicker.displayName = 'OnlineBookingWalkingDatePicker';
